import React, { useState } from "react";
import { Link } from "react-router-dom";
import { submitSurvey } from "./api";
import "./styles/base.css";
import "./styles/survey.css";
import {
  initialSurveyState,
  INVITE_OPTIONS,
  LEADERSHIP_ROLES,
  MORNING_GROUP_OPTIONS,
  MORNING_SESSION_OPTIONS,
  MUSIC_PREFERENCE_OPTIONS,
  RETURN_NEXT_YEAR_OPTIONS,
  SURVEY_SUCCESS_MESSAGE,
  validateSurveyForm,
  WEEK_RATING_OPTIONS,
  hasLeadershipInterest,
} from "./surveyUtils";

function Section({ title, subtitle, children }) {
  return (
    <section className="survey-section">
      <div className="survey-section__header">
        <h2>{title}</h2>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      <div className="survey-section__body">{children}</div>
    </section>
  );
}

function Field({ label, htmlFor, hint, error, required, children }) {
  return (
    <div className="field">
      <label className="field-label" htmlFor={htmlFor}>
        {label}
        {required ? <span className="required">*</span> : null}
      </label>
      {children}
      {hint ? <div className="hint">{hint}</div> : null}
      {error ? <div className="survey-error">{error}</div> : null}
    </div>
  );
}

function TextAreaField({ id, label, value, onChange, hint, error, required }) {
  return (
    <Field label={label} htmlFor={id} hint={hint} error={error} required={required}>
      <textarea
        id={id}
        className="input survey-textarea"
        value={value}
        onChange={onChange}
        rows={4}
      />
    </Field>
  );
}

function SelectField({ id, label, value, onChange, options, hint, error, required, placeholder }) {
  return (
    <Field label={label} htmlFor={id} hint={hint} error={error} required={required}>
      <select id={id} className="select" value={value} onChange={onChange}>
        <option value="">{placeholder || "Select one"}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </Field>
  );
}

export default function SurveyPage() {
  const [formData, setFormData] = useState(initialSurveyState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  const leadershipSelected = hasLeadershipInterest(formData);

  function updateField(field, value) {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  function handleRoleToggle(role) {
    setFormData((prev) => {
      const alreadySelected = prev.leadershipRoles.includes(role);
      const leadershipRoles = alreadySelected
        ? prev.leadershipRoles.filter((item) => item !== role)
        : [...prev.leadershipRoles, role];

      return {
        ...prev,
        leadershipRoles,
      };
    });
    setErrors((prev) => ({
      ...prev,
      leadershipName: undefined,
      leadershipEmail: undefined,
      leadershipPhone: undefined,
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const nextErrors = validateSurveyForm(formData);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setSubmitting(true);
    try {
      const result = await submitSurvey(formData);
      setSubmitResult(result);
      setFormData(initialSurveyState);
      setErrors({});
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setErrors({
        form:
          error instanceof Error ? error.message : "We could not submit the survey. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  }

  if (submitResult?.ok) {
    return (
      <div className="survey-page">
        <div className="form-container survey-shell">
          <div className="survey-success">
            <h1>Survey Submitted</h1>
            <p>{SURVEY_SUCCESS_MESSAGE}</p>
            {submitResult.submittedAt ? (
              <p className="small">Submitted at {new Date(submitResult.submittedAt).toLocaleString()}.</p>
            ) : null}
            <div className="survey-success__actions">
              <button type="button" className="btn btn-primary" onClick={() => setSubmitResult(null)}>
                Submit another response
              </button>
              <Link className="btn btn-secondary" to="/register">
                Go to registration
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="survey-page">
      <div className="form-container survey-shell">
        <header className="survey-hero">
          <div>
            <p className="survey-eyebrow">Camp Fusion 2026</p>
            <h1>Camp Fusion Survey</h1>
            <p className="survey-intro">
              Please fill out this survey to help us make next year’s camp even better! We value your honest feedback and will use it to plan improvements for the 2027 camp experience.
            </p>
          </div>
          <Link className="survey-link" to="/register">
            Registration is still available here
          </Link>
        </header>

        {errors.form ? <div className="survey-form-error">{errors.form}</div> : null}

        <form onSubmit={handleSubmit}>
          <Section
            title="Stay connected"
            subtitle="These contact fields are optional for general survey feedback."
          >
            <div className="two-col">
              <Field
                label="Email (optional)"
                htmlFor="contactEmail"
                error={errors.contactEmail}
              >
                <input
                  id="contactEmail"
                  className="input"
                  type="email"
                  value={formData.contactEmail}
                  onChange={(event) => updateField("contactEmail", event.target.value)}
                />
              </Field>
              <Field label="Phone number (optional)" htmlFor="contactPhone">
                <input
                  id="contactPhone"
                  className="input"
                  type="tel"
                  value={formData.contactPhone}
                  onChange={(event) => updateField("contactPhone", event.target.value)}
                />
              </Field>
            </div>
          </Section>

          <Section
            title="Overall Experience"
            subtitle="Tell us how camp felt overall and whether you hope to return."
          >
            <div className="two-col">
              <SelectField
                id="weekRating"
                label="On a scale from 1-10 (1 being the worst), how would you rate your week at camp?"
                value={formData.weekRating}
                onChange={(event) => updateField("weekRating", event.target.value)}
                options={WEEK_RATING_OPTIONS}
                error={errors.weekRating}
                required
              />
              <SelectField
                id="returnNextYear"
                label="Do you hope to come back next year?"
                value={formData.returnNextYear}
                onChange={(event) => updateField("returnNextYear", event.target.value)}
                options={RETURN_NEXT_YEAR_OPTIONS}
                error={errors.returnNextYear}
                required
              />
            </div>
          </Section>

          <Section
            title="Activities and Camp Life"
            subtitle="What stood out this year, and what would you change next time?"
          >
            <div className="two-col">
              <Field
                label="Favorite activity"
                htmlFor="favoriteActivity"
                error={errors.favoriteActivity}
              >
                <input
                  id="favoriteActivity"
                  className="input"
                  type="text"
                  value={formData.favoriteActivity}
                  onChange={(event) => updateField("favoriteActivity", event.target.value)}
                />
              </Field>
              <Field
                label="Least favorite activity"
                htmlFor="leastFavoriteActivity"
                error={errors.leastFavoriteActivity}
              >
                <input
                  id="leastFavoriteActivity"
                  className="input"
                  type="text"
                  value={formData.leastFavoriteActivity}
                  onChange={(event) => updateField("leastFavoriteActivity", event.target.value)}
                />
              </Field>
            </div>
            <TextAreaField
              id="activitySuggestions"
              label="Do you have any activity suggestions for next year?"
              value={formData.activitySuggestions}
              onChange={(event) => updateField("activitySuggestions", event.target.value)}
              error={errors.activitySuggestions}
            />
            <TextAreaField
              id="counselorFeedback"
              label="Was there something the counselors or other camp volunteers could have done different that would have made this week more enjoyable?"
              value={formData.counselorFeedback}
              onChange={(event) => updateField("counselorFeedback", event.target.value)}
              error={errors.counselorFeedback}
            />
          </Section>

          <Section
            title="Speakers and Services"
            subtitle="Help us plan next year’s speakers, worship, and morning sessions."
          >
            <TextAreaField
              id="speakerRecommendations"
              label="Do you have a recommendation for adult or youth speakers next year? Include for what age group."
              value={formData.speakerRecommendations}
              onChange={(event) => updateField("speakerRecommendations", event.target.value)}
              error={errors.speakerRecommendations}
            />
            <TextAreaField
              id="adultFeedback"
              label="Adults, did you enjoy your time and what can we do next year to make it better for you?"
              value={formData.adultFeedback}
              onChange={(event) => updateField("adultFeedback", event.target.value)}
              hint="Skip if not applicable."
            />
            <TextAreaField
              id="parentFeedback"
              label="Parents with small children, did you feel there was enough for them to do and what do you think would make next year better for them?"
              value={formData.parentFeedback}
              onChange={(event) => updateField("parentFeedback", event.target.value)}
              hint="Skip if not applicable."
            />
            <TextAreaField
              id="eveningEvangelistRequest"
              label="All ages, is there somebody you would like to request to be the evening evangelist?"
              value={formData.eveningEvangelistRequest}
              onChange={(event) => updateField("eveningEvangelistRequest", event.target.value)}
              error={errors.eveningEvangelistRequest}
            />
            <div className="two-col">
              <SelectField
                id="musicPreference"
                label="On a scale of 1 wanting all modern worship to 5 wanting all hymns/traditional worship, what is your music preference for evening service?"
                value={formData.musicPreference}
                onChange={(event) => updateField("musicPreference", event.target.value)}
                options={MUSIC_PREFERENCE_OPTIONS}
                error={errors.musicPreference}
                required
              />
              <SelectField
                id="morningGroup"
                label="Which group were you with during the morning session?"
                value={formData.morningGroup}
                onChange={(event) => updateField("morningGroup", event.target.value)}
                options={MORNING_GROUP_OPTIONS}
                error={errors.morningGroup}
                required
              />
            </div>
            <SelectField
              id="morningSessionEnjoyment"
              label="How did you enjoy the morning session?"
              value={formData.morningSessionEnjoyment}
              onChange={(event) => updateField("morningSessionEnjoyment", event.target.value)}
              options={MORNING_SESSION_OPTIONS}
              error={errors.morningSessionEnjoyment}
              required
            />
            <TextAreaField
              id="morningSessionExplanation"
              label="Please explain your previous answer."
              value={formData.morningSessionExplanation}
              onChange={(event) => updateField("morningSessionExplanation", event.target.value)}
              error={errors.morningSessionExplanation}
            />
          </Section>

          <Section
            title="Meals and Accommodations"
            subtitle="A few questions about practical parts of the week."
          >
            <TextAreaField
              id="mealComments"
              label="Do you have any comments and/or suggestions for meals?"
              value={formData.mealComments}
              onChange={(event) => updateField("mealComments", event.target.value)}
              error={errors.mealComments}
            />
            <TextAreaField
              id="accommodationsFeedback"
              label="Where did you sleep and how do you feel about the accommodations?"
              value={formData.accommodationsFeedback}
              onChange={(event) => updateField("accommodationsFeedback", event.target.value)}
              error={errors.accommodationsFeedback}
              required
            />
          </Section>

          <Section
            title="Looking ahead"
            subtitle="Tell us how camp could improve next year and who else should come."
          >
            <TextAreaField
              id="overallSuggestions"
              label="Suggestions for ANYTHING for 2026. Such as time changes, different way of doing service, etc."
              value={formData.overallSuggestions}
              onChange={(event) => updateField("overallSuggestions", event.target.value)}
              error={errors.overallSuggestions}
            />
            <SelectField
              id="inviteAudience"
              label="Do you have people in mind you would like to invite to come enjoy camp next year?"
              value={formData.inviteAudience}
              onChange={(event) => updateField("inviteAudience", event.target.value)}
              options={INVITE_OPTIONS}
              error={errors.inviteAudience}
              required
            />
          </Section>

          <Section
            title="Leadership Team Interest"
            subtitle="Choose any roles you are interested in. If you select one or more, we will require follow-up contact details."
          >
            <div className="survey-checkbox-grid">
              {LEADERSHIP_ROLES.map((role) => (
                <label key={role} className="survey-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.leadershipRoles.includes(role)}
                    onChange={() => handleRoleToggle(role)}
                  />
                  <span>{role}</span>
                </label>
              ))}
            </div>

            {leadershipSelected ? (
              <div className="survey-follow-up">
                <div className="survey-follow-up__title">Leadership follow-up contact</div>
                <div className="two-col">
                  <Field
                    label="Name"
                    htmlFor="leadershipName"
                    error={errors.leadershipName}
                    required
                  >
                    <input
                      id="leadershipName"
                      className="input"
                      type="text"
                      value={formData.leadershipName}
                      onChange={(event) => updateField("leadershipName", event.target.value)}
                    />
                  </Field>
                  <Field
                    label="Phone number"
                    htmlFor="leadershipPhone"
                    error={errors.leadershipPhone}
                    required
                  >
                    <input
                      id="leadershipPhone"
                      className="input"
                      type="tel"
                      value={formData.leadershipPhone}
                      onChange={(event) => updateField("leadershipPhone", event.target.value)}
                    />
                  </Field>
                </div>
                <Field
                  label="Email"
                  htmlFor="leadershipEmail"
                  error={errors.leadershipEmail}
                  required
                >
                  <input
                    id="leadershipEmail"
                    className="input"
                    type="email"
                    value={formData.leadershipEmail}
                    onChange={(event) => updateField("leadershipEmail", event.target.value)}
                  />
                </Field>
              </div>
            ) : null}
          </Section>

          <Section title="Anything else?" subtitle="Leave any final complaints, praises, or thoughts below.">
            <TextAreaField
              id="finalThoughts"
              label="Finally, if you have any other complaints, praises, or thoughts please leave them below."
              value={formData.finalThoughts}
              onChange={(event) => updateField("finalThoughts", event.target.value)}
              error={errors.finalThoughts}
            />
          </Section>

          <div className="form-footer survey-footer">
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? "Submitting..." : "Submit survey"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
